// "What the Report Will Print" — the form builder Preview's Review & Submit
// table (TASK-267.4, decision-76, board 06), replacing the old preview's
// JSON dump. Pure functions over the wizard's schema and answers, kept out
// of submission_wizard_controller.js so the wizard's preview branch stays a
// few lines.
//
// Choice answers print the option LABEL, looked up from the schema's own
// options, falling back to the raw stored value — the same rule decision-76
// sets for every report surface. Legacy options can store a value that
// differs from their label ("option_1" / "Communal"), so this matters even
// after new options store value == label.

const BLANK = "—"

function isBlank(value) {
  return value === undefined || value === null || value === "" ||
    (Array.isArray(value) && value.length === 0)
}

function optionLabel(field, value) {
  const options = field.options?.options || []
  const match = options.find(opt => String(opt.value) === String(value))
  return match?.label || String(value)
}

function productName(field, id) {
  const groups = field.options?.products_data || []
  for (const group of groups) {
    const product = (group.products || []).find(p => p.id === id)
    if (product) return product.name
  }
  return String(id)
}

function plural(count, noun) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`
}

// One answer as the report prints it — null when there is nothing to print.
export function answerText(field, value) {
  switch (field.field_type) {
    case "toggle":
      return value === true || value === "true" || value === "1" ? "Yes" : "No"
    case "select":
    case "radio":
      return isBlank(value) ? null : optionLabel(field, value)
    case "checkbox":
      if (isBlank(value)) return null
      return (Array.isArray(value) ? value : [ value ]).map(v => optionLabel(field, v)).join(", ")
    case "rating": {
      const rating = parseInt(value, 10)
      if (!rating) return null
      return `${rating} / ${field.options?.max_value || 5}`
    }
    case "photo":
      if (isBlank(value)) return null
      return plural(Array.isArray(value) ? value.length : 1, "photo")
    case "signature":
      return isBlank(value) ? null : "Signed"
    case "product_select":
      if (isBlank(value)) return null
      return (Array.isArray(value) ? value : [ value ]).map(id => productName(field, id)).join(", ")
    case "product_installer": {
      if (isBlank(value) || typeof value !== "object" || !value.product_type_id) return null
      const list = field.options?.product_type_data_list || []
      return list.find(pt => pt.id === value.product_type_id)?.name || "Recorded"
    }
    default:
      if (isBlank(value)) return null
      return typeof value === "object" ? "Recorded" : String(value)
  }
}

// Rows for every VISIBLE question, grouped under a heading per section (and
// per entry of a repeatable section). Hidden-by-logic questions are left
// out, exactly as the report leaves them out.
//
//   { sections, instanceCounts, isVisible(field, sectionKey, index),
//     getValue(fieldKey, sectionKey, index) }
//   -> [{ heading, rows: [{ label, text, blank }] }]
export function reportGroups({ sections, instanceCounts, isVisible, getValue }) {
  const groups = []

  ;(sections || []).forEach(section => {
    const indices = section.repeatable
      ? Array.from({ length: instanceCounts[section.section_key] || 1 }, (_, i) => i)
      : [ null ]

    indices.forEach(index => {
      const rows = []
      section.fields.forEach(field => {
        if (field.field_type === "section_break") return
        if (!isVisible(field, section.section_key, index)) return

        const text = answerText(field, getValue(field.field_key, section.section_key, index))
        rows.push({ label: field.label, text: text ?? BLANK, blank: text === null })
      })
      if (rows.length === 0) return

      const heading = index === null ? section.title : `${section.title} ${index + 1}`
      groups.push({ heading, rows })
    })
  })

  return groups
}
